'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import Icon from '@/components/ui/AppIcon';

const navLinks = [
  { label: 'Marketplace', href: '/marketplace' },
  { label: 'Community', href: '/community-feed' },
  { label: 'Pet Profiles', href: '/pet-profile' },
  { label: 'NFC Tags', href: '/nfc-tag-scan-lost-mode-view' },
];

export default function AppHeader() {
  const pathname = usePathname();
  const [cartCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/92 backdrop-blur-md border-b border-border shadow-card">
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-10 h-16 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2 no-underline flex-shrink-0">
          <img
            src="/assets/images/logo-full-1785153841359.png"
            alt="PetHub PH Logo"
            className="h-8 w-auto object-contain"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navLinks?.map((link) => (
            <Link
              key={`header-nav-${link?.label}`}
              href={link?.href}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors no-underline ${
                pathname === link?.href
                  ? 'nav-pill-active' :'text-body-text hover:text-foreground hover:bg-chip-bg'
              }`}
            >
              {link?.label}
            </Link>
          ))}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          {/* Search */}
          <button className="hidden sm:flex p-2 rounded-full hover:bg-chip-bg transition-colors" aria-label="Search">
            <Icon name="MagnifyingGlassIcon" size={18} className="text-body-text" />
          </button>

          {/* Notifications */}
          <button className="relative p-2 rounded-full hover:bg-chip-bg transition-colors" aria-label="Notifications">
            <Icon name="BellIcon" size={18} className="text-body-text" />
            <span className="absolute top-1 right-1 w-4 h-4 bg-brand-red text-white text-[9px] font-extrabold rounded-full flex items-center justify-center">3</span>
          </button>

          {/* Cart */}
          <Link href="/marketplace" className="relative p-2 rounded-full hover:bg-chip-bg transition-colors no-underline" aria-label="Cart">
            <Icon name="ShoppingCartIcon" size={18} className="text-body-text" />
            {cartCount > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-primary text-white text-[9px] font-extrabold rounded-full flex items-center justify-center">{cartCount}</span>
            )}
          </Link>

          {/* Auth buttons — desktop */}
          <div className="hidden lg:flex items-center gap-2 ml-2">
            <Link href="/sign-up-login-screen" className="btn-ghost text-sm no-underline py-2 px-3">
              Log in
            </Link>
            <Link href="/sign-up-login-screen" className="btn-primary text-sm no-underline py-2 px-4">
              Join Free
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 rounded-full hover:bg-chip-bg transition-colors"
            aria-label="Toggle menu"
          >
            <Icon name={mobileOpen ? 'XMarkIcon' : 'Bars3Icon'} size={20} />
          </button>
        </div>
      </div>
      {/* Mobile dropdown menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-border px-4 py-4 flex flex-col gap-1 fade-in">
          {navLinks?.map((link) => (
            <Link
              key={`mob-header-${link?.label}`}
              href={link?.href}
              className={`px-4 py-2.5 rounded-xl text-sm font-semibold no-underline transition-colors ${
                pathname === link?.href ? 'bg-primary-tint text-primary' : 'text-body-text hover:bg-muted'
              }`}
              onClick={() => setMobileOpen(false)}
            >
              {link?.label}
            </Link>
          ))}
          <div className="flex gap-2 mt-3 pt-3 border-t border-border">
            <Link href="/sign-up-login-screen" className="btn-outline text-sm no-underline flex-1 justify-center py-2.5">Log in</Link>
            <Link href="/sign-up-login-screen" className="btn-primary text-sm no-underline flex-1 justify-center py-2.5">Join Free</Link>
          </div>
        </div>
      )}
    </header>
  );
}